import { useTranslation } from '@/i18n/useTranslation';

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reviewsApi } from '@/api';
import { useAuth } from '@/auth/useAuth';
import { KpiCard } from '@/components/common/KpiCard';
import { KpiSkeletonGrid, TableSkeleton, ChartSkeleton } from '@/components/common/LoadingSkeleton';
import { InlineError } from '@/components/common/InlineError';
import { EmptyState } from '@/components/common/EmptyState';
import { ScanTrendChart } from '@/components/charts/ScanTrendChart';
import { useScanSummary, useScanTrend } from '@/hooks/useScanAnalytics';

const filters = {};

export function DashboardPage() {
  const { locale, t } = useTranslation();
  const { role } = useAuth();
  const admin = role === 'admin' || role === 'super_admin';
  const summary = useScanSummary(filters);
  const trend = useScanTrend(filters);
  const recent = useQuery({
    queryKey: ['scans', admin, 1, '', ''],
    queryFn: () => reviewsApi.listScans(admin, 1),
  });

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <header>
        <span className="section-kicker">{t('QUALITY CONTROL / OVERVIEW')}</span>
        <h1>{t('Saved inspection overview')}</h1>
        <p>
          {t(
            'For this release, each successfully saved inference is one inspection, including scans with no detections. Figures cover retained history only.',
          )}
        </p>
      </header>
      <section aria-label={t('Inspection counts')}>
        {summary.isLoading && <KpiSkeletonGrid />}
        {summary.isError && <InlineError error={summary.error} onRetry={() => summary.refetch()} />}
        {summary.data && (
          <div className="grid grid--kpis">
            <KpiCard
              label={t('Saved inspections')}
              value={summary.data.total_inspections.toLocaleString(locale)}
            />
            <KpiCard
              label={t('With detections')}
              value={summary.data.with_detections.toLocaleString(locale)}
              hint={t('Model found at least one defect')}
            />
            <KpiCard
              label={t('Total detections')}
              value={summary.data.total_detections.toLocaleString(locale)}
            />
            <KpiCard
              label={t('Reviewed inspections')}
              value={summary.data.reviewed_inspections.toLocaleString(locale)}
              hint={t('At least one human review')}
            />
          </div>
        )}
      </section>
      <section className="glass-panel">
        <h2>{t('Inspection activity')}</h2>
        {trend.isLoading && <ChartSkeleton />}
        {trend.isError && <InlineError error={trend.error} onRetry={() => trend.refetch()} />}
        {trend.data?.length === 0 && <EmptyState title={t('No saved inspections yet')} />}
        {trend.data && trend.data.length > 0 && <ScanTrendChart data={trend.data} />}
      </section>
      <section className="glass-panel">
        <div className="flex-between">
          <h2>{t('Recent inspections')}</h2>
          <Link to="/logs" className="btn btn--secondary btn--sm">
            {t('View all')}
          </Link>
        </div>
        {recent.isLoading && <TableSkeleton />}
        {recent.isError && <InlineError error={recent.error} onRetry={() => recent.refetch()} />}
        {recent.data?.items.length === 0 && <EmptyState title={t('No saved inspections yet')} />}
        {recent.data && recent.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <caption className="visually-hidden">{t('Recent saved inspections')}</caption>
              <thead>
                <tr>
                  <th>{t('Completed')}</th>
                  <th>{t('Model')}</th>
                  <th>{t('Detections')}</th>
                  <th>{t('Reviews')}</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.items.slice(0, 10).map((scan) => (
                  <tr key={scan.inference_id}>
                    <td>
                      {scan.completed_at ? new Date(scan.completed_at).toLocaleString(locale) : '—'}
                    </td>
                    <td>{scan.model_version ?? '—'}</td>
                    <td>{scan.detection_count}</td>
                    <td>{scan.review_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
